import { Toaster } from 'sonner';
import './App.css';
import Routers from './router/Routers';
import { ThemeProvider } from './components/theme/theme-provider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useNavigate } from 'react-router';
import { useEffect } from 'react';
import { logout, useAppDispatch } from './store';
import { toast } from 'sonner';
import { sentryUtils } from './utils/sentry';

const INACTIVITY_TIMEOUT = 60 * 1000 * 60; // 60 minutes

const useInactivityTimeout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    let timeout;

    const resetTimeout = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        dispatch(logout());
        toast.warning('You have been logged out due to inactivity.');
        navigate('/');
      }, INACTIVITY_TIMEOUT);
    };

    const events = ['mousemove', 'keydown', 'scroll', 'click'];

    const handleEvent = () => {
      resetTimeout();
    };

    events.forEach((event) => {
      window.addEventListener(event, handleEvent);
    });

    resetTimeout();

    return () => {
      clearTimeout(timeout);
      events.forEach((event) => {
        window.removeEventListener(event, handleEvent);
      });
    };
  }, [navigate]);
};

function App() {
  useInactivityTimeout();

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 0,
        staleTime: 5 * 60 * 1000, // Data is considered fresh for 5 minutes
      },
    },
  });

  // Set up Sentry user context when app loads
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.user?._id) {
      sentryUtils.setUser(user);
    }
  }, []);

  return (
    <div className="text-foreground bg-background">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
          <ReactQueryDevtools initialIsOpen={false} />
          <Routers />
          <Toaster richColors />
        </ThemeProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
